import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { DSTOffsetDay, DSTOffsetMonth, DSTOffsetWeek, StreamaxRecordOverwrite } from "@app/main/modules/foundation/_enums/camera-configs/CameraConfigEnums";
import { IAccount } from "@app/services/account.service";
import { ICameraDeviceParameterProfileSettings, IStreamaxCameraProfile, ITriggerMetricsOverride, StreamaxCameraProfileService } from "@app/services/camera/streamax-camera-profile-service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { mergeMap, tap } from "rxjs";
import './streamax-config-profile-editor.component.scss';

@UntilDestroy()
@Component({
    selector: 'streamax-config-profile-editor',
    template: require('./streamax-config-profile-editor.component.html')
})
export class StreamaxConfigProfileEditor implements OnChanges {
    @Input() profile: ICameraDeviceParameterProfileSettings;
    @Input() isNewAccount: boolean;
    @Input() timezones: ITimeZoneInfo[];
    @Input() accounts: IAccount[];
    @Input() accountIdsWithExistingProfile: number[];

    @Output() profileUpdated: EventEmitter<ICameraDeviceParameterProfileSettings> = new EventEmitter<ICameraDeviceParameterProfileSettings>();
    @Output() newProfileCreated: EventEmitter<IStreamaxCameraProfile> = new EventEmitter<IStreamaxCameraProfile>();
    @Output() profileIsInSavableState: EventEmitter<boolean> = new EventEmitter<boolean>();

    profileTimezone: ITimeZoneInfo;

    accountPicker: FormGroup;
    audioForm: FormGroup;
    ignitionDelayForm: FormGroup;
    videoLossForm: FormGroup;
    cameraCoveredForm: FormGroup;
    timezoneForm: FormGroup;
    accelAlarmGlobal: FormGroup;
    accelAlarmTrigger: FormGroup;
    brakeAlarmTrigger: FormGroup;
    leftAlarmTrigger: FormGroup;
    rightAlarmTrigger: FormGroup;
    //profileFormGroup: FormGroup = new FormGroup({});

    tableColumns: IVTableColumn<ITriggerMetricsOverride>[];
    tableRows: IVTableRow[];

    selectedRow: ITriggerMetricsOverride;

    selectedNewAccount: IAccount;

    overriddenVehicleTypes: VehicleType[] = [];

    constructor(
        private readonly streamaxCameraProfileService: StreamaxCameraProfileService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isNewAccount']) {
            this.profile = {
                AudioEnabled: false,
                CameraCoveredAlarm: false,
                VideoLossAlarm: true,
                IgnitionDelay: 300,
                RecordOverwrite: StreamaxRecordOverwrite.ByCapacity,
                TimeSettings: {
                    TimezoneId: "0A",
                    DSTEnabled: true,
                    OffSetStartMonth: DSTOffsetMonth.March,
                    OffSetEndMonth: DSTOffsetMonth.October,
                    OffSetStartWeek: DSTOffsetWeek.LastWeekOfMonth,
                    OffSetEndWeek: DSTOffsetWeek.LastWeekOfMonth,
                    OffSetStartDay: DSTOffsetDay.Sunday,
                    OffSetEndDay: DSTOffsetDay.Sunday,
                    OffSetStartHour: 1,
                    OffSetEndHour: 2,
                    OffSetStartMinute: 0,
                    OffSetEndMinute: 0
                },
                AlarmProfileMap: {
                    AlarmEnabled: true,
                    HardAccelerationTriggerEnabled: true,
                    HarshBrakingTriggerEnabled: true,
                    HarshLeftTurnTriggerEnabled: true,
                    HarshRightTurnTriggerEnabled: true
                }
            }

            this.updateTimezoneInfo();
        }

        if (changes['profile']) {
            this.overriddenVehicleTypes = this.profile.AlarmProfileMap.TriggerMetricOverrides?.map(o => o.VehicleType);

            this.updateTimezoneInfo();
        }
        
    }

    accountSearchPredicate(account: IAccount, text: string): boolean {
        return account.CompanyName.toLowerCase().includes(text.toLowerCase());
    }

    onAccountSelected($event: IAccount): void {
        this.selectedNewAccount = $event;
        this.accountPicker.controls.AccountPicker.setValue(this.selectedNewAccount);
        this.accountPicker.get('AccountPicker').updateValueAndValidity();
    }

    clearSlectedAccount(): void {
        this.selectedNewAccount = null;
        this.accountPicker.controls.AccountPicker.setValue(this.selectedNewAccount);
        this.accountPicker.get('AccountPicker').updateValueAndValidity();
    }

    saveNewProfile(): void {
        this.streamaxCameraProfileService
            .convertTimezoneInfoIdToStreamaxModel(this.profile.internalTimezoneInfo.Id)
            .pipe(
                untilDestroyed(this),
                mergeMap(streamaxTimezone => {
                    this.profile.TimeSettings = streamaxTimezone;
                    

                    const newProfileToSave: IStreamaxCameraProfile = {
                        AccountId: this.selectedNewAccount.Id,
                        IsActive: false,
                        Id: 0,
                        CameraDeviceParameterProfileSettings: this.profile,
                        Name: this.selectedNewAccount.CompanyName
                    };
                    
                    return this.streamaxCameraProfileService.postProfile(newProfileToSave);
                })
            )
            .subscribe({
                next: newProfile => {
                    this.newProfileCreated.emit(newProfile);
                }
            });
    }
    
    private updateTimezoneInfo(): void {
        if (!!this.profile.internalTimezoneInfo) {
            this.createFormCategories();
            return;
        }

        this.streamaxCameraProfileService.convertStreamaxTimezoneId(this.profile.TimeSettings?.TimezoneId).pipe(untilDestroyed(this), tap(timezoneInfoId => {
            this.profile.internalTimezoneInfo = this.timezones.find(t => t.Id === timezoneInfoId.timezoneId);

            this.createFormCategories();
        })).subscribe({
            error: (err) => console.error('Error updating timezone info:', err),
        });
    }

    getControlType(control: FormControl): string {
        if (control.value instanceof Boolean || typeof control.value === 'boolean') {
            return 'boolean';
        } else if (typeof control.value === 'number') {
            return 'number';
        } else {
            return 'text';
        }
    }

    private createFormCategories(): void {
        
        this.createAudioFormCategory();
        this.createIgnitionFormCategory();
        this.createVideoLossFormCategory();
        this.createCameraCoveredFormCategory();
        this.createTimezoneFormCategory();
        this.createGlobalAccelerationAlarmFormCategory();
        this.createAccelerationAlarmTriggerFormCategory();
        this.createBrakingAlarmTriggerFormCategory();
        this.createLeftAlarmTriggerFormCategory();
        this.createRightAlarmTriggerFormCategory();

        if (this.isNewAccount) {
            this.createAccountFormCategory();
        }
    }

    private updateProfile(status: string): void {
        if (status == "INVALID" || this.anyFormGroupIsInvaild()) {
            this.profileIsInSavableState.emit(false);
            return;
        }

        this.profileIsInSavableState.emit(true);
        this.profile = {
            ...this.profile,
            AudioEnabled: this.audioForm.get("AudioEnabled").value,
            IgnitionDelay: this.ignitionDelayForm.get("IgnitionDelay").value,
            VideoLossAlarm: this.videoLossForm.get("VideoLossAlarm").value,
            CameraCoveredAlarm: this.cameraCoveredForm.get("CameraCoveredAlarm").value,
            AlarmProfileMap: {
                AlarmEnabled: this.accelAlarmGlobal.get("AccelAlarmGlobal").value,
                HardAccelerationTriggerEnabled: this.accelAlarmTrigger.get("AccelAlarmTrigger").value,
                HarshBrakingTriggerEnabled: this.brakeAlarmTrigger.get("BrakeAlarmTrigger").value,
                HarshLeftTurnTriggerEnabled: this.leftAlarmTrigger.get("LeftAlarmTrigger").value,
                HarshRightTurnTriggerEnabled: this.rightAlarmTrigger.get("RightAlarmTrigger").value,
                TriggerMetricOverrides: this.profile.AlarmProfileMap?.TriggerMetricOverrides
            },
            updatedTimezoneInfo: this.timezoneForm.get("Timezone").value
        }

        this.profileUpdated.emit(this.profile);
    }

    private anyFormGroupIsInvaild(): boolean {
        return this.ignitionDelayForm.get("IgnitionDelay").invalid ||
            this.accountPicker?.get('AccountPicker')?.invalid;
    }

    private createAccountFormCategory(): void {
        this.accountPicker = new FormGroup({
            "AccountPicker": new FormControl(this.selectedNewAccount, { validators: [Validators.required, CustomFormValidators.accountHasConfig(this.accountIdsWithExistingProfile)] })
        });

        this.accountPicker.get("AccountPicker").markAsDirty();

        this.accountPicker.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createAccelerationAlarmTriggerFormCategory(): void {
        this.accelAlarmTrigger = new FormGroup({
            "AccelAlarmTrigger": new FormControl(this.profile.AlarmProfileMap.HardAccelerationTriggerEnabled)
        });

        this.accelAlarmTrigger.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createBrakingAlarmTriggerFormCategory(): void {
        this.brakeAlarmTrigger = new FormGroup({
            "BrakeAlarmTrigger": new FormControl(this.profile.AlarmProfileMap.HarshBrakingTriggerEnabled)
        });

        this.brakeAlarmTrigger.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createLeftAlarmTriggerFormCategory(): void {
        this.leftAlarmTrigger = new FormGroup({
            "LeftAlarmTrigger": new FormControl(this.profile.AlarmProfileMap.HarshLeftTurnTriggerEnabled)
        });

        this.leftAlarmTrigger.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createRightAlarmTriggerFormCategory(): void {
        this.rightAlarmTrigger = new FormGroup({
            "RightAlarmTrigger": new FormControl(this.profile.AlarmProfileMap.HarshRightTurnTriggerEnabled)
        });

        this.rightAlarmTrigger.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createGlobalAccelerationAlarmFormCategory(): void {
        this.accelAlarmGlobal = new FormGroup({
            "AccelAlarmGlobal": new FormControl(this.profile.AlarmProfileMap.AlarmEnabled)
        });
        
        this.accelAlarmGlobal.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createTimezoneFormCategory(): void {
        this.timezoneForm = new FormGroup({
            "Timezone": new FormControl(this.profile.internalTimezoneInfo, {
                validators: [Validators.required]
            })
        });

        this.timezoneForm.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createCameraCoveredFormCategory(): void {
        this.cameraCoveredForm = new FormGroup({
            "CameraCoveredAlarm": new FormControl(this.profile.CameraCoveredAlarm)
        });

        this.cameraCoveredForm.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            });
    }

    private createVideoLossFormCategory(): void {
        this.videoLossForm = new FormGroup({
            "VideoLossAlarm": new FormControl(this.profile.VideoLossAlarm)
        });

        this.videoLossForm.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            })
    }

    private createIgnitionFormCategory(): void {
        this.ignitionDelayForm = new FormGroup({
            "IgnitionDelay": new FormControl(this.profile.IgnitionDelay, { validators: [Validators.required, Validators.min(0), Validators.max(86399)] })
        });
        
        this.ignitionDelayForm.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            })
    }

    private createAudioFormCategory(): void {
        this.audioForm = new FormGroup({
            "AudioEnabled": new FormControl(this.profile.AudioEnabled)
        });

        this.audioForm.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.updateProfile(status);
            })
    }
}
